"use client";

import { useEffect } from "react";
import { TriangleAlert } from "lucide-react";
import "./globals.css";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { EmptyState } from "@/components/os/EmptyState";

/**
 * The last resort: the root layout itself failed, so this draws its own <html> and <body> and can
 * lean on nothing the layout provides (no session, no theme script — it draws light). The way out
 * is a full page load rather than a client-side Link: the app around it is what broke, so a fresh
 * load is the reliable way back.
 */
export default function GlobalError({ error, reset }: { error: Error & { digest?: string }; reset: () => void }) {
  useEffect(() => {
    console.error(error);
  }, [error]);

  return (
    <html lang="en">
      <body className="bg-background font-sans text-foreground antialiased">
        <div className="flex min-h-dvh items-center justify-center px-4 py-12">
          <Card className="w-full max-w-md">
            <EmptyState
              icon={TriangleAlert}
              title="Aura Core hit a problem"
              description={
                <>
                  Nothing you had saved is affected. Try again, or reload the dashboard.
                  {error.digest ? (
                    <>
                      {" "}
                      Reference <span className="font-mono">{error.digest}</span>.
                    </>
                  ) : null}
                </>
              }
              className="min-h-0 py-6"
            />
            <div className="flex flex-wrap justify-center gap-2 px-4 pb-2">
              <Button variant="outline" onClick={reset}>
                Try again
              </Button>
              <Button onClick={() => window.location.assign("/dashboard")}>Reload the dashboard</Button>
            </div>
          </Card>
        </div>
      </body>
    </html>
  );
}
